import { lazy } from "react";
import { Route, Navigate } from "react-router-dom";
import { lazyWithRetry } from "@/lib/lazyWithRetry";

const LegacyRedirect = lazyWithRetry(() => import("@/components/LegacyRedirect").then(m => ({ default: m.default })));
const CategoryPage = lazyWithRetry(() => import("@/pages/CategoryPage"));
const DynamicCategoryPage = lazyWithRetry(() => import("@/pages/DynamicCategoryPage"));

// L1 Category Pages
const HundPage = lazyWithRetry(() => import("@/pages/categories/HundPage"));
const KattPage = lazyWithRetry(() => import("@/pages/categories/KattPage"));
const SmadjurPage = lazyWithRetry(() => import("@/pages/categories/SmadjurPage"));
const FagelPage = lazyWithRetry(() => import("@/pages/categories/FagelPage"));
const AkvariumPage = lazyWithRetry(() => import("@/pages/categories/AkvariumPage"));
const ReptilPage = lazyWithRetry(() => import("@/pages/categories/ReptilPage"));
const HastPage = lazyWithRetry(() => import("@/pages/categories/HastPage"));

// L2 Hund
const HundFoderPage = lazyWithRetry(() => import("@/pages/categories/hund/FoderPage"));
const HundGodisSnacksPage = lazyWithRetry(() => import("@/pages/categories/hund/GodisSnacksPage"));
const HundTuggDentalPage = lazyWithRetry(() => import("@/pages/categories/hund/TuggDentalPage"));
const HundMatGodisPage = lazyWithRetry(() => import("@/pages/categories/hund/MatGodisPage"));
const HundLeksakerPage = lazyWithRetry(() => import("@/pages/categories/hund/LeksakerPage"));
const HundSovaVilaPage = lazyWithRetry(() => import("@/pages/categories/hund/SovaVilaPage"));
const HundKoppelHalsbandPage = lazyWithRetry(() => import("@/pages/categories/hund/KoppelHalsbandPage"));
const HundGodisTuggPage = lazyWithRetry(() => import("@/pages/categories/hund/GodisTuggPage"));
const HundVardHalsaPage = lazyWithRetry(() => import("@/pages/categories/hund/VardHalsaPage"));
const HundMatplatsPage = lazyWithRetry(() => import("@/pages/categories/hund/MatplatsPage"));
const HundKladerUtelivPage = lazyWithRetry(() => import("@/pages/categories/hund/KladerUtelivPage"));
const HundTransportSakerhetPage = lazyWithRetry(() => import("@/pages/categories/hund/TransportSakerhetPage"));

// New L3 landing pages (SEO money pages)
const SpannmalsfrittPage = lazyWithRetry(() => import("@/pages/categories/hund/l3/SpannmalsfrittPage"));
const AllergifoderPage = lazyWithRetry(() => import("@/pages/categories/hund/l3/AllergifoderPage"));
const NamnbrickorL3Page = lazyWithRetry(() => import("@/pages/categories/hund/l3/NamnbrickorPage"));
const FastingmedelPage = lazyWithRetry(() => import("@/pages/categories/hund/l3/FastingmedelPage"));
const HundHygienPage = lazyWithRetry(() => import("@/pages/categories/hund/l3/HygienPage"));
const HundTandvardPage = lazyWithRetry(() => import("@/pages/categories/hund/l3/TandvardPage"));
const AkvariumVaxterHubPage = lazyWithRetry(() => import("@/pages/categories/akvarium/VaxterHubPage"));
const AkvariumCo2HubPage = lazyWithRetry(() => import("@/pages/categories/akvarium/Co2HubPage"));
const ValjaHundfoderGuidePage = lazyWithRetry(() => import("@/pages/guides/ValjaHundfoderPage"));
const UndulatGuidePage = lazyWithRetry(() => import("@/pages/guides/UndulatGuidePage"));
const HundfoderLandingPage = lazyWithRetry(() => import("@/pages/landing/HundfoderLandingPage"));
const KattmatLandingPage = lazyWithRetry(() => import("@/pages/landing/KattmatLandingPage"));
const ReptilVarmeBelysningLandingPage = lazyWithRetry(() => import("@/pages/landing/ReptilVarmeBelysningLandingPage"));

// L2 Katt
const KattFoderPage = lazyWithRetry(() => import("@/pages/categories/katt/FoderPage"));
const KattGodisSnacksPage = lazyWithRetry(() => import("@/pages/categories/katt/GodisSnacksPage"));
const KattMatGodisPage = lazyWithRetry(() => import("@/pages/categories/katt/MatGodisPage"));
const KattKlosmoblerPage = lazyWithRetry(() => import("@/pages/categories/katt/KlosmoblerPage"));
const KattLeksakerPage = lazyWithRetry(() => import("@/pages/categories/katt/LeksakerPage"));
const KattSovaMysaPage = lazyWithRetry(() => import("@/pages/categories/katt/SovaMysaPage"));
const KattToalettHygienPage = lazyWithRetry(() => import("@/pages/categories/katt/ToalettHygienPage"));
const KattVardHalsaPage = lazyWithRetry(() => import("@/pages/categories/katt/VardHalsaPage"));
const KattTransportPage = lazyWithRetry(() => import("@/pages/categories/katt/TransportPage"));

// L2 Smådjur
const SmadjurMatGodisPage = lazyWithRetry(() => import("@/pages/categories/smadjur/MatGodisPage"));
const SmadjurBurarInredningPage = lazyWithRetry(() => import("@/pages/categories/smadjur/BurarInredningPage"));
const SmadjurStroHygienPage = lazyWithRetry(() => import("@/pages/categories/smadjur/StroHygienPage"));
const SmadjurLeksakerPage = lazyWithRetry(() => import("@/pages/categories/smadjur/LeksakerPage"));

// L2 Fågel
const FagelMatGodisPage = lazyWithRetry(() => import("@/pages/categories/fagel/MatGodisPage"));
const FagelBurarInredningPage = lazyWithRetry(() => import("@/pages/categories/fagel/BurarInredningPage"));
const FagelLeksakerPage = lazyWithRetry(() => import("@/pages/categories/fagel/LeksakerPage"));

// L2 Akvarium
const AkvariumFiskmatPage = lazyWithRetry(() => import("@/pages/categories/akvarium/FiskmatPage"));
const AkvariumAkvarierTeknikPage = lazyWithRetry(() => import("@/pages/categories/akvarium/AkvarierTeknikPage"));
const AkvariumInredningPage = lazyWithRetry(() => import("@/pages/categories/akvarium/InredningPage"));

// L2 Reptil
const ReptilMatFoderPage = lazyWithRetry(() => import("@/pages/categories/reptil/MatFoderPage"));
const ReptilTerrarierTeknikPage = lazyWithRetry(() => import("@/pages/categories/reptil/TerrarierTeknikPage"));

// L2 Häst
const HastFoderGodisPage = lazyWithRetry(() => import("@/pages/categories/hast/FoderGodisPage"));
const HastRidningUtrustningPage = lazyWithRetry(() => import("@/pages/categories/hast/RidningUtrustningPage"));
const HastRyktVardPage = lazyWithRetry(() => import("@/pages/categories/hast/RyktVardPage"));
const HastStallHagnPage = lazyWithRetry(() => import("@/pages/categories/hast/StallHagnPage"));

// L3 Hund
const HundTorrfoderPage = lazyWithRetry(() => import("@/pages/categories/hund/l3/TorrfoderPage"));
const HundVatfoderPage = lazyWithRetry(() => import("@/pages/categories/hund/l3/VatfoderPage"));
const HundGodisSnacksL3Page = lazyWithRetry(() => import("@/pages/categories/hund/l3/GodisSnacksPage"));
const HundBollarPage = lazyWithRetry(() => import("@/pages/categories/hund/l3/BollarPage"));
const HundRepDragPage = lazyWithRetry(() => import("@/pages/categories/hund/l3/RepDragPage"));
const HundAktiveringPage = lazyWithRetry(() => import("@/pages/categories/hund/l3/AktiveringPage"));
const HundbaddarPage = lazyWithRetry(() => import("@/pages/categories/hund/l3/HundbaddarPage"));
const HundFiltarPage = lazyWithRetry(() => import("@/pages/categories/hund/l3/FiltarPage"));
const BurarTransportPage = lazyWithRetry(() => import("@/pages/categories/hund/l3/BurarTransportPage"));
const HundKoppelPage = lazyWithRetry(() => import("@/pages/categories/hund/l3/KoppelPage"));
const HalsbandPage = lazyWithRetry(() => import("@/pages/categories/hund/l3/HalsbandPage"));
const SelarPage = lazyWithRetry(() => import("@/pages/categories/hund/l3/SelarPage"));
const ReflexerBelysningPage = lazyWithRetry(() => import("@/pages/categories/hund/l3/ReflexerBelysningPage"));
const HundMjukdjurPage = lazyWithRetry(() => import("@/pages/categories/hund/l3/MjukdjurPage"));
const BarfFarskfoderPage = lazyWithRetry(() => import("@/pages/categories/hund/l3/BarfFarskfoderPage"));
const ValpfoderPage = lazyWithRetry(() => import("@/pages/categories/hund/l3/ValpfoderPage"));
const VuxenfoderPage = lazyWithRetry(() => import("@/pages/categories/hund/l3/VuxenfoderPage"));
const SeniorfoderPage = lazyWithRetry(() => import("@/pages/categories/hund/l3/SeniorfoderPage"));
const SpecialfoderPage = lazyWithRetry(() => import("@/pages/categories/hund/l3/SpecialfoderPage"));
const BeloningsgodisPage = lazyWithRetry(() => import("@/pages/categories/hund/l3/BeloningsgodisPage"));
const TuggbenPage = lazyWithRetry(() => import("@/pages/categories/hund/l3/TuggbenPage"));
const NaturligtTuggPage = lazyWithRetry(() => import("@/pages/categories/hund/l3/NaturligtTuggPage"));
const DentalPage = lazyWithRetry(() => import("@/pages/categories/hund/l3/DentalPage"));
const KosttillskottHundPage = lazyWithRetry(() => import("@/pages/categories/hund/l3/KosttillskottHundPage"));
const PalsvardHundPage = lazyWithRetry(() => import("@/pages/categories/hund/l3/PalsvardHundPage"));
const LoppaMaskHundPage = lazyWithRetry(() => import("@/pages/categories/hund/l3/LoppaMaskHundPage"));
const MatskallarPage = lazyWithRetry(() => import("@/pages/categories/hund/l3/MatskallarPage"));
const VattenautomaterPage = lazyWithRetry(() => import("@/pages/categories/hund/l3/VattenautomaterPage"));
const TackenJackorPage = lazyWithRetry(() => import("@/pages/categories/hund/l3/TackenJackorPage"));
const SkorTassPage = lazyWithRetry(() => import("@/pages/categories/hund/l3/SkorTassPage"));
const TransportburarPage = lazyWithRetry(() => import("@/pages/categories/hund/l3/TransportburarPage"));
const BilsakerhetPage = lazyWithRetry(() => import("@/pages/categories/hund/l3/BilsakerhetPage"));

// L3 Katt
const KattTorrfoderPage = lazyWithRetry(() => import("@/pages/categories/katt/l3/TorrfoderKattPage"));
const KattVatfoderPage = lazyWithRetry(() => import("@/pages/categories/katt/l3/VatfoderKattPage"));
const KlostradPage = lazyWithRetry(() => import("@/pages/categories/katt/l3/KlostradPage"));
const KattbaddarPage = lazyWithRetry(() => import("@/pages/categories/katt/l3/KattbaddarPage"));
const KattsandPage = lazyWithRetry(() => import("@/pages/categories/katt/l3/KattsandPage"));
const KattleksakerPage = lazyWithRetry(() => import("@/pages/categories/katt/l3/KattleksakerPage"));
const IglooGrottorPage = lazyWithRetry(() => import("@/pages/categories/katt/l3/IglooGrottorPage"));
const KattgodisPage = lazyWithRetry(() => import("@/pages/categories/katt/l3/KattgodisPage"));
const KattladaPage = lazyWithRetry(() => import("@/pages/categories/katt/l3/KattladaPage"));
const KattungePage = lazyWithRetry(() => import("@/pages/categories/katt/l3/KattungePage"));
const VuxenKattPage = lazyWithRetry(() => import("@/pages/categories/katt/l3/VuxenKattPage"));
const SeniorKattPage = lazyWithRetry(() => import("@/pages/categories/katt/l3/SeniorKattPage"));
const SpecialfoderKattPage = lazyWithRetry(() => import("@/pages/categories/katt/l3/SpecialfoderKattPage"));
const KattgrasPage = lazyWithRetry(() => import("@/pages/categories/katt/l3/KattgrasPage"));
const KosttillskottKattPage = lazyWithRetry(() => import("@/pages/categories/katt/l3/KosttillskottKattPage"));
const LaserpekarePage = lazyWithRetry(() => import("@/pages/categories/katt/l3/LaserpekarePage"));
const AktiveringKattPage = lazyWithRetry(() => import("@/pages/categories/katt/l3/AktiveringKattPage"));
const PalsvardKattPage = lazyWithRetry(() => import("@/pages/categories/katt/l3/PalsvardKattPage"));
const KlorKattPage = lazyWithRetry(() => import("@/pages/categories/katt/l3/KlorKattPage"));
const LoppaMaskPage = lazyWithRetry(() => import("@/pages/categories/katt/l3/LoppaMaskPage"));

// L3 Smådjur
const SmadjurPelletsPage = lazyWithRetry(() => import("@/pages/categories/smadjur/l3/PelletsPage"));
const SmadjurHoGrasPage = lazyWithRetry(() => import("@/pages/categories/smadjur/l3/HoGrasPage"));
const FroblandningarSmadjurPage = lazyWithRetry(() => import("@/pages/categories/smadjur/l3/FroblandningarSmadjurPage"));
const SmadjurgodisPage = lazyWithRetry(() => import("@/pages/categories/smadjur/l3/SmadjurgodisPage"));
const SmadjurBurarPage = lazyWithRetry(() => import("@/pages/categories/smadjur/l3/BurarPage"));
const SmadjurInredningPage = lazyWithRetry(() => import("@/pages/categories/smadjur/l3/InredningPage"));

// L3 Fågel
const FagelFroblandningarPage = lazyWithRetry(() => import("@/pages/categories/fagel/l3/FroblandningarPage"));
const FagelburarPage = lazyWithRetry(() => import("@/pages/categories/fagel/l3/FagelburarPage"));
const FagelgodisPage = lazyWithRetry(() => import("@/pages/categories/fagel/l3/FagelgodisPage"));
const FagelinredningPage = lazyWithRetry(() => import("@/pages/categories/fagel/l3/FagelinredningPage"));
const MineralerFagelPage = lazyWithRetry(() => import("@/pages/categories/fagel/l3/MineralerFagelPage"));
const SittpinnarPage = lazyWithRetry(() => import("@/pages/categories/fagel/l3/SittpinnarPage"));
const LeksakerFagelPage = lazyWithRetry(() => import("@/pages/categories/fagel/l3/LeksakerFagelPage"));
const GungorPage = lazyWithRetry(() => import("@/pages/categories/fagel/l3/GungorPage"));
const KlattraPage = lazyWithRetry(() => import("@/pages/categories/fagel/l3/KlattraPage"));

// L3 Akvarium
const FiskflingorPage = lazyWithRetry(() => import("@/pages/categories/akvarium/l3/FiskflingorPage"));
const AkvarierPage = lazyWithRetry(() => import("@/pages/categories/akvarium/l3/AkvarierPage"));
const FilterPumparPage = lazyWithRetry(() => import("@/pages/categories/akvarium/l3/FilterPumparPage"));
const AkvariumBelysningPage = lazyWithRetry(() => import("@/pages/categories/akvarium/l3/BelysningPage"));
const AkvariumDekorationPage = lazyWithRetry(() => import("@/pages/categories/akvarium/l3/DekorationPage"));
const AkvariumVarmarePage = lazyWithRetry(() => import("@/pages/categories/akvarium/l3/VarmarePage"));
const AkvariumVaxterPage = lazyWithRetry(() => import("@/pages/categories/akvarium/l3/VaxterPage"));
const AkvariumBottenmaterialPage = lazyWithRetry(() => import("@/pages/categories/akvarium/l3/BottenmaterialPage"));
const AkvariumVattenvardPage = lazyWithRetry(() => import("@/pages/categories/akvarium/l3/VattenvardPage"));

// L3 Reptil
const FrystorkatPage = lazyWithRetry(() => import("@/pages/categories/reptil/l3/FrystorkatPage"));
const TerrarierPage = lazyWithRetry(() => import("@/pages/categories/reptil/l3/TerrarierPage"));
const VarmePage = lazyWithRetry(() => import("@/pages/categories/reptil/l3/VarmePage"));
const UVBelysningPage = lazyWithRetry(() => import("@/pages/categories/reptil/l3/UVBelysningPage"));
const ReptildekorationPage = lazyWithRetry(() => import("@/pages/categories/reptil/l3/ReptildekorationPage"));
const KosttillskottPage = lazyWithRetry(() => import("@/pages/categories/reptil/l3/KosttillskottPage"));

export const categoryRoutes = (
  <>
    {/* L1 */}
    <Route path="/hund" element={<HundPage />} />
    <Route path="/katt" element={<KattPage />} />
    <Route path="/smadjur" element={<SmadjurPage />} />
    <Route path="/fagel" element={<FagelPage />} />
    <Route path="/akvarium" element={<AkvariumPage />} />
    <Route path="/reptil" element={<ReptilPage />} />
    <Route path="/hast" element={<HastPage />} />

    {/* L2 Häst */}
    <Route path="/hast/foder-godis" element={<HastFoderGodisPage />} />
    <Route path="/hast/ridning-utrustning" element={<HastRidningUtrustningPage />} />
    <Route path="/hast/rykt-vard" element={<HastRyktVardPage />} />
    <Route path="/hast/stall-hagn" element={<HastStallHagnPage />} />

    {/* === Google Ads sitelink aliases (200 OK, NO redirect) ===
        These short SEO-friendly slugs are used as Google Ads sitelink destinations.
        They render the same component as their canonical category but at the alias URL,
        so Google Ads sees a fully functional 200 response.
        Pre-rendered HTML for crawlers includes <link rel="canonical"> pointing to the
        canonical category in render-meta/index.ts (AD_SITELINK_ALIASES). */}
    <Route path="/hundfoder" element={<HundFoderPage />} />
    <Route path="/hund-foder" element={<HundFoderPage />} />
    {/* High-conversion paid-traffic landing variant for hundfoder */}
    <Route path="/hundfoder-landing" element={<HundfoderLandingPage />} />
    <Route path="/landing/hundfoder" element={<HundfoderLandingPage />} />
    <Route path="/kattmat-landing" element={<KattmatLandingPage />} />
    <Route path="/landing/kattmat" element={<KattmatLandingPage />} />
    <Route path="/reptil-varme-belysning-landing" element={<ReptilVarmeBelysningLandingPage />} />
    <Route path="/landing/reptil-varme-belysning" element={<ReptilVarmeBelysningLandingPage />} />
    <Route path="/hundleksaker" element={<HundLeksakerPage />} />
    <Route path="/hund-leksaker" element={<HundLeksakerPage />} />
    <Route path="/hundgodis-tuggben" element={<HundGodisSnacksPage />} />
    <Route path="/koppel-halsband" element={<HundKoppelHalsbandPage />} />
    <Route path="/kattmat" element={<KattFoderPage />} />
    <Route path="/katt-foder" element={<KattFoderPage />} />
    <Route path="/kattleksaker" element={<KattLeksakerPage />} />
    <Route path="/katt-leksaker" element={<KattLeksakerPage />} />

    {/* L2 Hund */}
    <Route path="/hund/foder" element={<HundFoderPage />} />
    <Route path="/hund/godis-snacks" element={<HundGodisSnacksPage />} />
    <Route path="/hund/godis" element={<HundGodisSnacksPage />} />
    <Route path="/hund/tugg-dental" element={<HundTuggDentalPage />} />
    <Route path="/hund/mat-godis" element={<HundMatGodisPage />} />
    <Route path="/hund/leksaker" element={<HundLeksakerPage />} />
    <Route path="/hund/sova-vila" element={<HundSovaVilaPage />} />
    <Route path="/hund/koppel-halsband" element={<HundKoppelHalsbandPage />} />
    <Route path="/hund/godis-tugg" element={<HundGodisTuggPage />} />
    <Route path="/hund/vard-halsa" element={<HundVardHalsaPage />} />
    <Route path="/hund/matplats" element={<HundMatplatsPage />} />
    <Route path="/hund/klader-uteliv" element={<HundKladerUtelivPage />} />
    <Route path="/hund/transport-sakerhet" element={<HundTransportSakerhetPage />} />

    {/* L2 Katt */}
    <Route path="/katt/foder" element={<KattFoderPage />} />
    <Route path="/katt/godis-snacks" element={<KattGodisSnacksPage />} />
    <Route path="/katt/mat-godis" element={<KattMatGodisPage />} />
    <Route path="/katt/klosmobler" element={<KattKlosmoblerPage />} />
    <Route path="/katt/leksaker" element={<KattLeksakerPage />} />
    <Route path="/katt/sova-mysa" element={<KattSovaMysaPage />} />
    <Route path="/katt/toalett-hygien" element={<KattToalettHygienPage />} />
    <Route path="/katt/vard-halsa" element={<KattVardHalsaPage />} />
    <Route path="/katt/transport" element={<KattTransportPage />} />

    {/* L2 Smådjur */}
    <Route path="/smadjur/mat-godis" element={<SmadjurMatGodisPage />} />
    <Route path="/smadjur/burar-inredning" element={<SmadjurBurarInredningPage />} />
    <Route path="/smadjur/stro-hygien" element={<SmadjurStroHygienPage />} />
    <Route path="/smadjur/leksaker" element={<SmadjurLeksakerPage />} />

    {/* L2 Fågel */}
    <Route path="/fagel/mat-godis" element={<FagelMatGodisPage />} />
    <Route path="/fagel/burar-inredning" element={<FagelBurarInredningPage />} />
    <Route path="/fagel/leksaker" element={<FagelLeksakerPage />} />

    {/* L2 Akvarium */}
    <Route path="/akvarium/fiskmat" element={<AkvariumFiskmatPage />} />
    <Route path="/akvarium/akvarier-teknik" element={<AkvariumAkvarierTeknikPage />} />
    <Route path="/akvarium/inredning" element={<AkvariumInredningPage />} />

    {/* L2 Reptil */}
    <Route path="/reptil/mat-foder" element={<ReptilMatFoderPage />} />
    <Route path="/reptil/terrarier-teknik" element={<ReptilTerrarierTeknikPage />} />

    {/* L3 Hund */}
    <Route path="/hund/foder/torrfoder" element={<HundTorrfoderPage />} />
    <Route path="/hund/foder/vatfoder" element={<HundVatfoderPage />} />
    <Route path="/hund/foder/barf-farskfoder" element={<BarfFarskfoderPage />} />
    <Route path="/hund/foder/valpfoder" element={<ValpfoderPage />} />
    <Route path="/hund/foder/vuxenfoder" element={<VuxenfoderPage />} />
    <Route path="/hund/foder/seniorfoder" element={<SeniorfoderPage />} />
    <Route path="/hund/foder/specialfoder" element={<SpecialfoderPage />} />
    <Route path="/hund/foder/spannmalsfritt" element={<SpannmalsfrittPage />} />
    <Route path="/hund/foder/allergi" element={<AllergifoderPage />} />
    <Route path="/hund/koppel-halsband/namnbrickor" element={<NamnbrickorL3Page />} />
    <Route path="/hund/vard-halsa/fastingmedel" element={<FastingmedelPage />} />
    <Route path="/akvarium/vaxter" element={<AkvariumVaxterHubPage />} />
    <Route path="/akvarium/co2" element={<AkvariumCo2HubPage />} />
    <Route path="/guide/valja-hundfoder" element={<ValjaHundfoderGuidePage />} />
    <Route path="/guide/undulat" element={<UndulatGuidePage />} />
    <Route path="/hund/godis-snacks/godis" element={<HundGodisSnacksL3Page />} />
    <Route path="/hund/godis-snacks/beloningsgodis" element={<BeloningsgodisPage />} />
    <Route path="/hund/tugg-dental/tuggben" element={<TuggbenPage />} />
    <Route path="/hund/tugg-dental/naturligt-tugg" element={<NaturligtTuggPage />} />
    <Route path="/hund/tugg-dental/dental" element={<DentalPage />} />
    <Route path="/hund/leksaker/bollar" element={<HundBollarPage />} />
    <Route path="/hund/leksaker/rep-dragleksaker" element={<HundRepDragPage />} />
    <Route path="/hund/leksaker/aktivering" element={<HundAktiveringPage />} />
    <Route path="/hund/leksaker/mjukdjur" element={<HundMjukdjurPage />} />
    <Route path="/hund/sova-vila/hundbaddar" element={<HundbaddarPage />} />
    <Route path="/hund/sova-vila/filtar" element={<HundFiltarPage />} />
    <Route path="/hund/sova-vila/burar-transport" element={<BurarTransportPage />} />
    <Route path="/hund/koppel-halsband/koppel" element={<HundKoppelPage />} />
    <Route path="/hund/koppel-halsband/halsband" element={<HalsbandPage />} />
    <Route path="/hund/koppel-halsband/selar" element={<SelarPage />} />
    <Route path="/hund/koppel-halsband/reflexer-belysning" element={<ReflexerBelysningPage />} />
    <Route path="/hund/vard-halsa/kosttillskott" element={<KosttillskottHundPage />} />
    <Route path="/hund/vard-halsa/palsvard" element={<PalsvardHundPage />} />
    <Route path="/hund/vard-halsa/loppa-mask" element={<LoppaMaskHundPage />} />
    <Route path="/hund/vard-halsa/hygien" element={<HundHygienPage />} />
    <Route path="/hund/vard-halsa/tandvard" element={<HundTandvardPage />} />
    {/* Top-level shortcuts */}
    <Route path="/hund/hygien" element={<Navigate to="/hund/vard-halsa/hygien" replace />} />
    <Route path="/hund/tandvard" element={<Navigate to="/hund/vard-halsa/tandvard" replace />} />
    <Route path="/hund/matplats/matskallar" element={<MatskallarPage />} />
    <Route path="/hund/matplats/vattenautomater" element={<VattenautomaterPage />} />
    <Route path="/hund/klader-uteliv/tacken" element={<TackenJackorPage />} />
    <Route path="/hund/klader-uteliv/skor" element={<SkorTassPage />} />
    <Route path="/hund/transport-sakerhet/transportburar" element={<TransportburarPage />} />
    <Route path="/hund/transport-sakerhet/bilsakerhet" element={<BilsakerhetPage />} />

    {/* L3 Hund - legacy URLs */}
    <Route path="/hund/mat-godis/torrfoder" element={<LegacyRedirect canonicalPath="/hund/foder/torrfoder"><HundTorrfoderPage /></LegacyRedirect>} />
    <Route path="/hund/mat-godis/vatfoder" element={<LegacyRedirect canonicalPath="/hund/foder/vatfoder"><HundVatfoderPage /></LegacyRedirect>} />
    <Route path="/hund/mat-godis/godis-snacks" element={<LegacyRedirect canonicalPath="/hund/godis-snacks/godis"><HundGodisSnacksL3Page /></LegacyRedirect>} />
    <Route path="/hund/mat-godis/barf-farskfoder" element={<LegacyRedirect canonicalPath="/hund/foder/barf-farskfoder"><BarfFarskfoderPage /></LegacyRedirect>} />
    <Route path="/hund/mat-godis/valpfoder" element={<LegacyRedirect canonicalPath="/hund/foder/valpfoder"><ValpfoderPage /></LegacyRedirect>} />
    <Route path="/hund/mat-godis/seniorfoder" element={<LegacyRedirect canonicalPath="/hund/foder/seniorfoder"><SeniorfoderPage /></LegacyRedirect>} />
    <Route path="/hund/mat-godis/specialfoder" element={<LegacyRedirect canonicalPath="/hund/foder/specialfoder"><SpecialfoderPage /></LegacyRedirect>} />
    <Route path="/hund/godis-tugg/beloningsgodis" element={<LegacyRedirect canonicalPath="/hund/godis-snacks/beloningsgodis"><BeloningsgodisPage /></LegacyRedirect>} />
    <Route path="/hund/godis-tugg/tuggben" element={<LegacyRedirect canonicalPath="/hund/tugg-dental/tuggben"><TuggbenPage /></LegacyRedirect>} />
    <Route path="/hund/godis-tugg/naturligt-tugg" element={<LegacyRedirect canonicalPath="/hund/tugg-dental/naturligt-tugg"><NaturligtTuggPage /></LegacyRedirect>} />
    <Route path="/hund/godis-tugg/dental" element={<LegacyRedirect canonicalPath="/hund/tugg-dental/dental"><DentalPage /></LegacyRedirect>} />

    {/* L3 Katt */}
    <Route path="/katt/foder/torrfoder" element={<KattTorrfoderPage />} />
    <Route path="/katt/foder/vatfoder" element={<KattVatfoderPage />} />
    <Route path="/katt/foder/kattunge" element={<KattungePage />} />
    <Route path="/katt/foder/vuxen" element={<VuxenKattPage />} />
    <Route path="/katt/foder/senior" element={<SeniorKattPage />} />
    <Route path="/katt/foder/specialfoder" element={<SpecialfoderKattPage />} />
    <Route path="/katt/godis-snacks/godis" element={<KattgodisPage />} />
    <Route path="/katt/godis-snacks/kattgras" element={<KattgrasPage />} />
    <Route path="/katt/klosmobler/klostrad" element={<KlostradPage />} />
    <Route path="/katt/sova-mysa/kattbaddar" element={<KattbaddarPage />} />
    <Route path="/katt/sova-mysa/igloo-grottor" element={<IglooGrottorPage />} />
    <Route path="/katt/toalett-hygien/kattsand" element={<KattsandPage />} />
    <Route path="/katt/toalett-hygien/kattlada" element={<KattladaPage />} />
    <Route path="/katt/leksaker/kattleksaker" element={<KattleksakerPage />} />
    <Route path="/katt/leksaker/laserpekare" element={<LaserpekarePage />} />
    <Route path="/katt/leksaker/aktivering" element={<AktiveringKattPage />} />
    <Route path="/katt/vard-halsa/kosttillskott" element={<KosttillskottKattPage />} />
    <Route path="/katt/vard-halsa/palsvard" element={<PalsvardKattPage />} />
    <Route path="/katt/vard-halsa/klor" element={<KlorKattPage />} />
    <Route path="/katt/vard-halsa/loppa-mask" element={<LoppaMaskPage />} />

    {/* L3 Katt - legacy URLs */}
    <Route path="/katt/mat-godis/torrfoder" element={<LegacyRedirect canonicalPath="/katt/foder/torrfoder"><KattTorrfoderPage /></LegacyRedirect>} />
    <Route path="/katt/mat-godis/vatfoder" element={<LegacyRedirect canonicalPath="/katt/foder/vatfoder"><KattVatfoderPage /></LegacyRedirect>} />
    <Route path="/katt/mat-godis/godis" element={<LegacyRedirect canonicalPath="/katt/godis-snacks/godis"><KattgodisPage /></LegacyRedirect>} />
    <Route path="/katt/mat-godis/kattunge" element={<LegacyRedirect canonicalPath="/katt/foder/kattunge"><KattungePage /></LegacyRedirect>} />
    <Route path="/katt/mat-godis/senior" element={<LegacyRedirect canonicalPath="/katt/foder/senior"><SeniorKattPage /></LegacyRedirect>} />
    <Route path="/katt/mat-godis/specialfoder" element={<LegacyRedirect canonicalPath="/katt/foder/specialfoder"><SpecialfoderKattPage /></LegacyRedirect>} />
    <Route path="/katt/mat-godis/kattgras" element={<LegacyRedirect canonicalPath="/katt/godis-snacks/kattgras"><KattgrasPage /></LegacyRedirect>} />

    {/* Legacy redirects from 404 logs */}
    <Route path="/katt/aktivering" element={<Navigate to="/katt/leksaker/aktivering" replace />} />
    <Route path="/katt/specialfoder" element={<Navigate to="/katt/foder/specialfoder" replace />} />
    <Route path="/katt/sova-och-mysa" element={<Navigate to="/katt/sova-mysa" replace />} />
    <Route path="/katt/blotmat-katt" element={<Navigate to="/katt/foder/vatfoder" replace />} />
    <Route path="/katt/toalett-hygien/kattlada-tillbehor" element={<Navigate to="/katt/toalett-hygien/kattlada" replace />} />
    <Route path="/hund/naturligt-tugg" element={<Navigate to="/hund/tugg-dental/naturligt-tugg" replace />} />
    <Route path="/hund/barf-och-farskfoder" element={<Navigate to="/hund/foder/barf-farskfoder" replace />} />
    {/* /hund/tandvard handled above as L3 redirect */}
    <Route path="/hund/hund-i-hemmet/hundbaddar" element={<Navigate to="/hund/sova-vila/hundbaddar" replace />} />
    <Route path="/hund/hund-i-hemmet/foderautomater-till-hund" element={<Navigate to="/hund/matplats" replace />} />
    <Route path="/hund/hundleksaker/pipleksaker-till-hund" element={<Navigate to="/hund/leksaker" replace />} />
    <Route path="/hund/hundgodis/tuggben" element={<Navigate to="/hund/tugg-dental/tuggben" replace />} />
    <Route path="/fagel/froblandningar" element={<Navigate to="/fagel/mat-godis/froblandningar" replace />} />
    <Route path="/fagel/battisto-och-fagelsand" element={<Navigate to="/fagel/mat-godis" replace />} />
    <Route path="/smadjur/chinchilla" element={<Navigate to="/sok?q=chinchilla" replace />} />
    <Route path="/presentkort" element={<Navigate to="/sok?q=presentkort" replace />} />

    {/* More legacy WooCommerce redirects from 404 logs */}
    <Route path="/hund/till-hundvalpen/valpfoder-till-hundvalpen" element={<Navigate to="/hund/foder/valpfoder" replace />} />
    <Route path="/hund/hundvard/tandvard-hund" element={<Navigate to="/hund/vard-halsa" replace />} />
    <Route path="/hund/hundvard/klo-tassvard" element={<Navigate to="/hund/vard-halsa" replace />} />
    <Route path="/hund/hundgodis/beloningsgodis" element={<Navigate to="/hund/godis-snacks/beloningsgodis" replace />} />
    <Route path="/hund/koppel" element={<Navigate to="/hund/koppel-halsband/koppel" replace />} />
    <Route path="/hund/koppel-och-halsband" element={<Navigate to="/hund/koppel-halsband" replace />} />
    <Route path="/hund/tacken" element={<Navigate to="/hund/klader-uteliv/tacken" replace />} />
    <Route path="/hund/specialfoder" element={<Navigate to="/hund/foder/specialfoder" replace />} />
    <Route path="/hund/transport/hundburar" element={<Navigate to="/hund/transport-sakerhet/transportburar" replace />} />
    <Route path="/hund/transport/hundvaskor" element={<Navigate to="/hund/transport-sakerhet" replace />} />
    <Route path="/hund/transport/aka-bat-med-hund" element={<Navigate to="/hund/transport-sakerhet" replace />} />
    <Route path="/hund/hund-i-bil/sakerhetsbalte-hund" element={<Navigate to="/hund/transport-sakerhet/bilsakerhet" replace />} />
    <Route path="/hund/hund-i-hemmet/hundmatskalar" element={<Navigate to="/hund/matplats/matskallar" replace />} />
    <Route path="/hund/hundfoder-mat/torrfoder-for-hund" element={<Navigate to="/hund/foder/torrfoder" replace />} />
    <Route path="/hund/hundfoder-mat/spannmalsfritt-hundfoder" element={<Navigate to="/hund/foder/spannmalsfritt" replace />} />
    <Route path="/katt/kosttillskott" element={<Navigate to="/katt/vard-halsa/kosttillskott" replace />} />
    <Route path="/katt/kattgras" element={<Navigate to="/katt/godis-snacks/kattgras" replace />} />
    <Route path="/katt/laserpekare" element={<Navigate to="/katt/leksaker/laserpekare" replace />} />
    <Route path="/katt/klostrad" element={<Navigate to="/katt/klosmobler/klostrad" replace />} />
    <Route path="/katt/klosmobler-till-katten" element={<Navigate to="/katt/klosmobler" replace />} />
    <Route path="/katt/klosmobler-till-katten/klospelare" element={<Navigate to="/katt/klosmobler/klostrad" replace />} />
    <Route path="/katt/klosmobler-till-katten/klosbrador" element={<Navigate to="/katt/klosmobler" replace />} />
    <Route path="/katt/kattlador-tillbehor/kattspadar" element={<Navigate to="/katt/toalett-hygien/kattlada" replace />} />
    <Route path="/katt/kattlador-tillbehor/kattlador-utan-tak" element={<Navigate to="/katt/toalett-hygien/kattlada" replace />} />
    <Route path="/katt/kattlador-tillbehor/pasar-till-kattlada" element={<Navigate to="/katt/toalett-hygien/kattlada" replace />} />
    <Route path="/katt/kattlador-tillbehor/matta-till-kattlada" element={<Navigate to="/katt/toalett-hygien/kattlada" replace />} />
    <Route path="/katt/kattlador-tillbehor/kattsand" element={<Navigate to="/katt/toalett-hygien/kattsand" replace />} />
    <Route path="/katt/kattleksaker/hus-tunnlar" element={<Navigate to="/katt/leksaker/kattleksaker" replace />} />
    <Route path="/katt/kattleksaker/kattspon-vippor" element={<Navigate to="/katt/leksaker/kattleksaker" replace />} />
    <Route path="/katt/kattmat/torrfoder-till-katt" element={<Navigate to="/katt/foder/torrfoder" replace />} />
    <Route path="/katt/kattmat/spannmalsfri-kattmat" element={<Navigate to="/katt/foder/specialfoder" replace />} />
    <Route path="/katt/kattburar/kattvaskor" element={<Navigate to="/katt/transport" replace />} />
    <Route path="/katt/kattbaddar-sangar/kattsangar-kattbaddar-sangar" element={<Navigate to="/katt/sova-mysa/kattbaddar" replace />} />
    <Route path="/katt/kattluckor" element={<Navigate to="/sok?q=kattlucka" replace />} />
    <Route path="/fagel/godis" element={<Navigate to="/fagel/mat-godis/godis" replace />} />
    <Route path="/fagel/sittpinnar" element={<Navigate to="/fagel/burar-inredning/sittpinnar" replace />} />
    <Route path="/smadjur/degu" element={<Navigate to="/sok?q=degu" replace />} />
    <Route path="/reptil/mat-och-foder" element={<Navigate to="/reptil/mat-foder" replace />} />
    <Route path="/reptil/foder/frukt-mat" element={<Navigate to="/reptil/mat-foder" replace />} />
    <Route path="/reptil/luftfuktighet" element={<Navigate to="/reptil/terrarier-teknik" replace />} />
    <Route path="/till-promenaden" element={<Navigate to="/hund/koppel-halsband" replace />} />
    <Route path="/prenumerera-spara" element={<Navigate to="/" replace />} />

    {/* Additional 404 fixes from GSC 2026-04-06 */}
    <Route path="/hund/hundfoder-mat" element={<Navigate to="/hund/foder" replace />} />
    <Route path="/hund/hundleksaker" element={<Navigate to="/hund/leksaker" replace />} />
    <Route path="/hund/hundleksaker/bollar-till-hund" element={<Navigate to="/hund/leksaker/bollar" replace />} />
    <Route path="/hund/hundleksaker/mjuka-hundleksaker" element={<Navigate to="/hund/leksaker/mjukdjur" replace />} />
    <Route path="/hund/hundleksaker/rep-dragleksaker" element={<Navigate to="/hund/leksaker/rep-dragleksaker" replace />} />
    <Route path="/hund/hundleksaker/vattenleksaker-hund" element={<Navigate to="/hund/leksaker" replace />} />
    <Route path="/hund/hundgodis/hundkex" element={<Navigate to="/hund/godis-snacks" replace />} />
    <Route path="/hund/hundklader/designa-sjalv-klader" element={<Navigate to="/hund/klader-uteliv" replace />} />
    <Route path="/hund/hundklader/hundjackor-hundtacken" element={<Navigate to="/hund/klader-uteliv/tacken" replace />} />
    <Route path="/hund/hundklader/hundtrojor-hundklader" element={<Navigate to="/hund/klader-uteliv" replace />} />
    <Route path="/hund/hundklader/regnklader-for-hund" element={<Navigate to="/hund/klader-uteliv" replace />} />
    <Route path="/hund/hundklader/sov-mysklader" element={<Navigate to="/hund/klader-uteliv" replace />} />
    <Route path="/hund/hundvard/palsvard-hund" element={<Navigate to="/hund/vard-halsa/palsvard" replace />} />
    <Route path="/hund/hundvard/skvatt-tikskydd" element={<Navigate to="/hund/vard-halsa" replace />} />
    <Route path="/hund/klader-och-skydd" element={<Navigate to="/hund/klader-uteliv" replace />} />
    <Route path="/hund/mat-godis/vatfoder" element={<Navigate to="/hund/foder/vatfoder" replace />} />
    <Route path="/hund/pepups" element={<Navigate to="/hund/godis-snacks" replace />} />
    <Route path="/hund/halsband" element={<Navigate to="/hund/koppel-halsband/halsband" replace />} />
    <Route path="/hund/till-hundvalpen/valphalsband" element={<Navigate to="/hund/koppel-halsband/halsband" replace />} />
    <Route path="/hund/till-hundvalpen/valpleksaker/*" element={<Navigate to="/hund/leksaker" replace />} />
    <Route path="/hund/till-promenaden/bajspasar-tillbehor" element={<Navigate to="/hund/koppel-halsband" replace />} />
    <Route path="/hund/till-promenaden/hundhalsband" element={<Navigate to="/hund/koppel-halsband/halsband" replace />} />
    <Route path="/hund/till-promenaden/hundkoppel/justerbara-koppel" element={<Navigate to="/hund/koppel-halsband/koppel" replace />} />
    <Route path="/hund/till-promenaden/hundkoppel/utstallningskoppel" element={<Navigate to="/hund/koppel-halsband/koppel" replace />} />
    <Route path="/hund/till-promenaden/hundvagnar" element={<Navigate to="/hund/transport-sakerhet" replace />} />
    <Route path="/hund/transport-sakerhet/bilselar" element={<Navigate to="/hund/transport-sakerhet/bilsakerhet" replace />} />
    <Route path="/hund/transport/aka-bat-med-hund" element={<Navigate to="/hund/transport-sakerhet" replace />} />
    <Route path="/hund/transport/hundvaskor" element={<Navigate to="/hund/transport-sakerhet" replace />} />
    <Route path="/hast/stall-hagn/stallartiklar" element={<Navigate to="/hast/stall-hagn" replace />} />
    <Route path="/katt/kattleksaker" element={<Navigate to="/katt/leksaker" replace />} />
    <Route path="/katt/kattleksaker/kattspon-vippor" element={<Navigate to="/katt/leksaker/kattleksaker" replace />} />
    <Route path="/katt/kattluckor/*" element={<Navigate to="/sok?q=kattlucka" replace />} />
    <Route path="/katt/kattmat/blotmat-till-katt" element={<Navigate to="/katt/foder/vatfoder" replace />} />
    <Route path="/katt/kattmat/farskfoder-till-katt" element={<Navigate to="/katt/foder" replace />} />
    <Route path="/katt/kattmat/spannmalsfri-kattmat" element={<Navigate to="/katt/foder/specialfoder" replace />} />
    <Route path="/katt/kattmat/torrfoder-till-katt" element={<Navigate to="/katt/foder/torrfoder" replace />} />
    <Route path="/katt/kattbaddar-sangar/kattigloos-katthus" element={<Navigate to="/katt/sova-mysa/igloo-grottor" replace />} />
    <Route path="/katt/kattburar/kattvaskor" element={<Navigate to="/katt/transport" replace />} />
    <Route path="/katt/klostrad-klosmobler" element={<Navigate to="/katt/klosmobler" replace />} />
    <Route path="/katt/klosmobler-till-katten/klosmobler" element={<Navigate to="/katt/klosmobler" replace />} />
    <Route path="/katt/koppel-halsband/selar" element={<Navigate to="/sok?q=kattsele" replace />} />
    <Route path="/katt/till-kattungen/torrfoder-till-kattunge" element={<Navigate to="/katt/foder/kattunge" replace />} />
    <Route path="/smadjur/stro-hygien/rengoring" element={<Navigate to="/smadjur/stro-hygien" replace />} />
    <Route path="/smadjur/stro-hygien/stro" element={<Navigate to="/smadjur/stro-hygien" replace />} />
    <Route path="/fagel/fagelleksaker/aktivitetsleksaker-fagel" element={<Navigate to="/fagel/leksaker/fagelleksaker" replace />} />

    {/* Batch 2026-04-09 redirects from crawler 404 logs */}
    <Route path="/hund/hygien-vard" element={<Navigate to="/hund/vard-halsa" replace />} />
    <Route path="/hund/selar" element={<Navigate to="/hund/koppel-halsband/selar" replace />} />
    <Route path="/hund/mjukdjur" element={<Navigate to="/hund/leksaker/mjukdjur" replace />} />
    <Route path="/hund/filtar" element={<Navigate to="/hund/sova-vila/filtar" replace />} />
    <Route path="/hund/sova-vila/kuddar" element={<Navigate to="/hund/sova-vila" replace />} />
    {/* Magnusson redirect (slug mismatch) */}
    <Route path="/varumarke/magnusson" element={<Navigate to="/varumarke/magnussons" replace />} />
    <Route path="/smadjur/hus-och-burinredning" element={<Navigate to="/smadjur/burar-inredning" replace />} />
    <Route path="/blogg/cane-corso-guide" element={<Navigate to="/blogg" replace />} />
    <Route path="/blogg/golden-retriever-guide" element={<Navigate to="/blogg" replace />} />
    <Route path="/blogg/basta-hundfoder-2024" element={<Navigate to="/blogg" replace />} />
    <Route path="/blogg/kanin-som-husdjur-guide" element={<Navigate to="/blogg" replace />} />
    {/* /logga-in handled in staticRoutes — do NOT redirect here */}

    {/* ═══════════════════════════════════════════════════════ */}
    {/* Legacy 404 redirects from crawler logs                 */}
    {/* ═══════════════════════════════════════════════════════ */}

    {/* Katt legacy subcategories */}
    <Route path="/katt/aktivering" element={<Navigate to="/katt/leksaker" replace />} />
    <Route path="/katt/specialfoder" element={<Navigate to="/katt/foder" replace />} />
    <Route path="/katt/kosttillskott" element={<Navigate to="/katt/foder" replace />} />
    <Route path="/katt/sova-och-mysa" element={<Navigate to="/katt" replace />} />
    <Route path="/katt/mat-godis/kattgras" element={<Navigate to="/katt/foder" replace />} />
    <Route path="/katt/mat-godis/senior" element={<Navigate to="/katt/foder" replace />} />
    <Route path="/katt/kattgras" element={<Navigate to="/katt/foder" replace />} />
    <Route path="/katt/toalett-hygien/kattlada-tillbehor" element={<Navigate to="/katt/toalett-hygien" replace />} />

    {/* Hund legacy subcategories */}
    <Route path="/hund/barf-och-farskfoder" element={<Navigate to="/hund/foder" replace />} />
    <Route path="/hund/naturligt-tugg" element={<Navigate to="/hund/godis-tugg" replace />} />
    {/* /hund/tandvard handled above as L3 redirect */}
    <Route path="/hund/specialfoder" element={<Navigate to="/hund/foder" replace />} />
    <Route path="/hund/hundvard/tandvard-hund" element={<Navigate to="/hund" replace />} />
    <Route path="/hund/hund-i-hemmet/hundbaddar" element={<Navigate to="/hund" replace />} />
    <Route path="/hund/hund-i-hemmet/foderautomater-till-hund" element={<Navigate to="/hund/foder" replace />} />
    <Route path="/hund/hundleksaker/pipleksaker-till-hund" element={<Navigate to="/hund/leksaker" replace />} />
    <Route path="/hund/mat-godis/vatfoder" element={<Navigate to="/hund/foder" replace />} />
    <Route path="/hund/koppel-och-halsband" element={<Navigate to="/hund/koppel-halsband" replace />} />
    <Route path="/hund/halsband" element={<Navigate to="/hund/koppel-halsband" replace />} />
    <Route path="/hund/tacken" element={<Navigate to="/hund" replace />} />
    <Route path="/hund/till-hundvalpen/valpfoder-till-hundvalpen" element={<Navigate to="/hund/foder" replace />} />

    {/* Smådjur legacy subcategories */}
    <Route path="/smadjur/chinchilla" element={<Navigate to="/smadjur" replace />} />
    <Route path="/smadjur/degu" element={<Navigate to="/smadjur" replace />} />

    {/* Fågel legacy subcategories */}
    <Route path="/fagel/froblandningar" element={<Navigate to="/fagel" replace />} />
    <Route path="/fagel/battisto-och-fagelsand" element={<Navigate to="/fagel" replace />} />
    <Route path="/fagel/godis" element={<Navigate to="/fagel" replace />} />
    <Route path="/fagel/sittpinnar" element={<Navigate to="/fagel" replace />} />

    {/* Reptil legacy subcategories */}
    <Route path="/reptil/mat-och-foder" element={<Navigate to="/reptil" replace />} />
    <Route path="/reptil/foder/frukt-mat" element={<Navigate to="/reptil" replace />} />
    <Route path="/reptil/luftfuktighet" element={<Navigate to="/reptil" replace />} />

    {/* General legacy pages */}
    <Route path="/presentkort" element={<Navigate to="/sok?q=presentkort" replace />} />
    {/* /logga-in handled in staticRoutes — do NOT redirect here */}

    {/* Batch 2026-04-17 — fresh 404s from crawler logs */}
    <Route path="/bli-leverantor" element={<Navigate to="/kontakt" replace />} />
    <Route path="/hast/foder-godis/hastgodis" element={<Navigate to="/hast/foder-godis" replace />} />
    <Route path="/smadjur/mat-godis/kosttillskott" element={<Navigate to="/smadjur/mat-godis" replace />} />

    {/* Batch 2026-04-18 — new 404s from crawler logs */}
    <Route path="/hundgodis-tuggben" element={<Navigate to="/hund/tugg-dental/tuggben" replace />} />
    <Route path="/koppel-halsband" element={<Navigate to="/hund/koppel-halsband" replace />} />
    <Route path="/katt-foder" element={<Navigate to="/katt/foder" replace />} />
    <Route path="/katt/kattvard-och-tillskott" element={<Navigate to="/katt/vard-halsa" replace />} />
    <Route path="/katt/kattsangar/*" element={<Navigate to="/katt/sova-mysa" replace />} />
    <Route path="/medlem" element={<Navigate to="/logga-in" replace />} />
    <Route path="/medlemskap" element={<Navigate to="/logga-in" replace />} />
    <Route path="/jamfor" element={<Navigate to="/produkter" replace />} />
    <Route path="/tips-advices/*" element={<Navigate to="/blogg" replace />} />
    <Route path="/tips-advices" element={<Navigate to="/blogg" replace />} />
    <Route path="/customer_authentication/*" element={<Navigate to="/logga-in" replace />} />

    {/* Missing legacy subcategories from 404 logs */}
    <Route path="/hund/hygien-vard" element={<Navigate to="/hund" replace />} />

    {/* Legacy WooCommerce /produkt-kategori/ paths */}
    <Route path="/produkt-kategori/hund/*" element={<Navigate to="/hund" replace />} />
    <Route path="/produkt-kategori/katt/*" element={<Navigate to="/katt" replace />} />
    <Route path="/produkt-kategori/smadjur/*" element={<Navigate to="/smadjur" replace />} />
    <Route path="/produkt-kategori/fagel/*" element={<Navigate to="/fagel" replace />} />
    <Route path="/produkt-kategori/akvarium/*" element={<Navigate to="/akvarium" replace />} />
    <Route path="/produkt-kategori/reptil/*" element={<Navigate to="/reptil" replace />} />
    <Route path="/produkt-kategori/hast/*" element={<Navigate to="/hast" replace />} />
    <Route path="/produkt-kategori/*" element={<Navigate to="/produkter" replace />} />

    {/* Legacy /katt/kattmat/ catch-all (subpaths have specific redirects above) */}
    <Route path="/katt/kattmat" element={<Navigate to="/katt/foder" replace />} />
    <Route path="/katt/kattmat/*" element={<Navigate to="/katt/foder" replace />} />

    {/* Junk/external URLs */}
    <Route path="/cdn/*" element={<Navigate to="/" replace />} />
    <Route path="/checkouts/*" element={<Navigate to="/" replace />} />

    {/* Legacy /collections/ paths */}
    <Route path="/collections/all" element={<Navigate to="/produkter" replace />} />
    <Route path="/collections/smygstart-pa-black-week.atom" element={<Navigate to="/produkter" replace />} />
    <Route path="/collections/all.atom" element={<Navigate to="/produkter" replace />} />
    <Route path="/collections/frontpage.atom" element={<Navigate to="/produkter" replace />} />
    <Route path="/collections/*" element={<Navigate to="/produkter" replace />} />


    {/* L3 Smådjur */}
    <Route path="/smadjur/mat-godis/pellets" element={<SmadjurPelletsPage />} />
    <Route path="/smadjur/mat-godis/ho-gras" element={<SmadjurHoGrasPage />} />
    <Route path="/smadjur/mat-godis/froblandningar" element={<FroblandningarSmadjurPage />} />
    <Route path="/smadjur/mat-godis/godis" element={<SmadjurgodisPage />} />
    <Route path="/smadjur/burar-inredning/burar" element={<SmadjurBurarPage />} />
    <Route path="/smadjur/burar-inredning/inredning" element={<SmadjurInredningPage />} />

    {/* L3 Fågel */}
    <Route path="/fagel/mat-godis/froblandningar" element={<FagelFroblandningarPage />} />
    <Route path="/fagel/mat-godis/godis" element={<FagelgodisPage />} />
    <Route path="/fagel/mat-godis/mineraler" element={<MineralerFagelPage />} />
    <Route path="/fagel/burar-inredning/fagelburar" element={<FagelburarPage />} />
    <Route path="/fagel/burar-inredning/inredning" element={<FagelinredningPage />} />
    <Route path="/fagel/burar-inredning/sittpinnar" element={<SittpinnarPage />} />
    <Route path="/fagel/leksaker/fagelleksaker" element={<LeksakerFagelPage />} />
    <Route path="/fagel/leksaker/gungor" element={<GungorPage />} />
    <Route path="/fagel/leksaker/klattra" element={<KlattraPage />} />

    {/* L3 Akvarium */}
    <Route path="/akvarium/fiskmat/fiskflingor" element={<FiskflingorPage />} />
    <Route path="/akvarium/akvarier-teknik/akvarier" element={<AkvarierPage />} />
    <Route path="/akvarium/akvarier-teknik/filter-pumpar" element={<FilterPumparPage />} />
    <Route path="/akvarium/akvarier-teknik/belysning" element={<AkvariumBelysningPage />} />
    <Route path="/akvarium/akvarier-teknik/varmare" element={<AkvariumVarmarePage />} />
    <Route path="/akvarium/akvarier-teknik/vattenvard" element={<AkvariumVattenvardPage />} />
    <Route path="/akvarium/inredning/dekoration" element={<AkvariumDekorationPage />} />
    <Route path="/akvarium/inredning/vaxter" element={<AkvariumVaxterPage />} />
    <Route path="/akvarium/inredning/bottenmaterial" element={<AkvariumBottenmaterialPage />} />

    {/* L3 Reptil */}
    <Route path="/reptil/mat-foder/frystorkat" element={<FrystorkatPage />} />
    <Route path="/reptil/mat-foder/kosttillskott" element={<KosttillskottPage />} />
    <Route path="/reptil/terrarier-teknik/terrarier" element={<TerrarierPage />} />
    <Route path="/reptil/terrarier-teknik/varme" element={<VarmePage />} />
    <Route path="/reptil/terrarier-teknik/uv-belysning" element={<UVBelysningPage />} />
    <Route path="/reptil/terrarier-teknik/dekoration" element={<ReptildekorationPage />} />

    {/* Dynamic L3 catch-all */}
    <Route path="/:l1/:l2/:l3" element={<DynamicCategoryPage />} />

    {/* Generic category fallback */}
    <Route path="/:category" element={<CategoryPage />} />
    <Route path="/:category/:subcategory" element={<CategoryPage />} />
  </>
);
